import { z } from "zod";
import { ConfirmationTerminalStateSchema } from "./confirmations.js";
import { HubChatAttachmentSchema } from "./hubchat.js";
import { AgentIdSchema } from "./primitives.js";

/**
 * Session (conversation) RPCs. Hub exposes participant-scoped reads over an
 * agent's conversations and their transcripts:
 *
 *   - session.list    — recent conversations for an agent, all statuses
 *                       (active / expired / reset), with per-conversation
 *                       metadata and the stored participant roster
 *   - session.history — the full transcript for one conversation, windowed
 *                       (a `before`/`limit` reverse-pagination cursor) and
 *                       run through the gateway's secret-redaction
 *   - session.exists  — metadata-only existence probe for one conversation;
 *                       never reads or transmits transcript bytes
 *
 * Archive/unarchive changes list placement, while the separate sharing routes
 * add or remove Hub-chat participants. Transcript, usage, and audit history
 * survive either operation untouched.
 */

// ---------------------------------------------------------------------------
// Shared shapes
// ---------------------------------------------------------------------------

/** A distinct human sender recorded in a conversation's participant roster. */
export const SessionParticipantSchema = z.object({
  platform: z.string(),
  userId: z.string(),
  displayName: z.string().optional(),
});
export type SessionParticipantView = z.infer<typeof SessionParticipantSchema>;

export const ConversationParticipantsDataSchema = z.object({
  participants: z.array(SessionParticipantSchema),
  changed: z.boolean().optional(),
});
export type ConversationParticipantsData = z.infer<typeof ConversationParticipantsDataSchema>;

export const ConversationLeaveDataSchema = z.object({ left: z.boolean() });
export type ConversationLeaveData = z.infer<typeof ConversationLeaveDataSchema>;

export const ConversationAudienceUserSchema = z.object({
  userId: z.string().min(1),
  displayName: z.string().min(1),
});
export type ConversationAudienceUser = z.infer<typeof ConversationAudienceUserSchema>;

export const ConversationAudienceDataSchema = z.object({
  users: z.array(ConversationAudienceUserSchema),
});
export type ConversationAudienceData = z.infer<typeof ConversationAudienceDataSchema>;

export const SessionStatusSchema = z.enum(["active", "expired", "reset"]);
export type SessionStatusView = z.infer<typeof SessionStatusSchema>;

/**
 * What produced a conversation, and therefore which lists it belongs in.
 *
 * `all` is spelled explicitly rather than being what an omitted filter means,
 * so a background conversation class added later cannot appear in the default
 * rail by inheriting silence.
 */
export const WorkspaceConversationKindSchema = z.enum(["chat", "scheduled-run"]);
export type WorkspaceConversationKind = z.infer<typeof WorkspaceConversationKindSchema>;

export const ConversationKindFilterSchema = z.enum(["chat", "scheduled-run", "all"]);
export type ConversationKindFilter = z.infer<typeof ConversationKindFilterSchema>;

/** Per-conversation summary row rendered in the recent-conversations list. */
const SessionSummarySchema = z.object({
  sessionId: z.string(),
  sessionKey: z.string(),
  /** Stable session name: the first user message (never changes as the conversation grows). */
  title: z.string(),
  /**
   * Recency line: the LAST renderable turn, prefixed with the agent's name
   * (`Ada: …`) when the agent spoke last. Empty when no renderable turn.
   */
  preview: z.string(),
  platform: z.string(),
  channelLabel: z.string().optional(),
  status: SessionStatusSchema,
  createdAt: z.string(),
  accessedAt: z.string(),
  messageCount: z.number().int().nonnegative(),
  participants: z.array(SessionParticipantSchema),
  confirmationMode: z.enum(["manual", "bypass"]).optional(),
  /**
   * When this conversation was archived out of the lists; absent while it is
   * visible. A row knows its own state, so the recovery surface does not have
   * to infer it from which list the row arrived in.
   */
  archivedAt: z.string().optional(),
  /** Scheduled conversations remain under Runs after human replies. */
  kind: WorkspaceConversationKindSchema,
  /** Only explicit `agent` means specialist sharing; omission remains participant-scoped. */
  audience: z.enum(["participants", "agent"]).optional(),
  scheduledTask: z.object({ id: z.string(), name: z.string().optional() }).optional(),
});
export type SessionSummaryView = z.infer<typeof SessionSummarySchema>;

export const ConversationResolutionDataSchema = z.object({ conversation: SessionSummarySchema });
export const ConversationCreateDataSchema = z.object({
  sessionId: z.string().uuid(),
  recordId: z.string().uuid(),
  sessionKey: z.string(),
});

/**
 * One tool invocation derived at read time from the transcript's persisted
 * `toolCall` / `toolResult` blocks. `paramsSummary`, `argsText` and
 * `resultText` are redacted + bounded at the gateway boundary with the same
 * treatment the live `tool_status` events apply — raw tool arguments and raw
 * tool results never cross this wire.
 * `incomplete` means the transcript records the call but no terminal result
 * (aborted turn, crash mid-tool); the live vocabulary's `running` is
 * deliberately not reused because nothing is running in a persisted view.
 */
/** One piece of an assistant entry, in recorded order. */
const SessionBlockSchema = z.union([
  z.object({ kind: z.literal("text"), text: z.string() }),
  z.object({ kind: z.literal("tool"), toolCallId: z.string() }),
]);
export type SessionBlockView = z.infer<typeof SessionBlockSchema>;

const SessionToolEventSchema = z.object({
  /** Correlation id (`toolCall.id` ↔ `toolResult.toolCallId`) when recorded. */
  toolCallId: z.string().optional(),
  toolName: z.string(),
  paramsSummary: z.string(),
  /**
   * `awaiting_approval`: the call is still waiting on a decision. Its only
   * recorded result is the permission gate's, which reads as an ordinary
   * success by content, so this value is what stops a reloaded parked turn
   * claiming the call already ran.
   */
  status: z.enum(["completed", "failed", "incomplete", "awaiting_approval"]),
  /** Expanded, redacted argument preview; absent for argument-less calls. */
  argsText: z.string().optional(),
  /** Expanded, redacted result preview; absent for `incomplete` and empty results. */
  resultText: z.string().optional(),
  timing: z.object({ startedAt: z.number(), completedAt: z.number().optional() }).optional(),
});
export type SessionToolEventView = z.infer<typeof SessionToolEventSchema>;

/**
 * A decided tool approval recorded in the session transcript at decision
 * time. `toolName` and `summary` carry the same already-redacted values the
 * live `confirmation_request` card showed, so replay renders the decision in
 * the vocabulary the decider saw. `resolvedByName` is the decider's display
 * name resolved at decision time — a durable label that survives later
 * directory changes; absent for expiry/cancellation terminals and when the
 * gateway holds no directory row for the decider.
 */
const SessionApprovalEventSchema = z.object({
  confirmationId: z.string(),
  toolCallId: z.string().optional(),
  toolName: z.string(),
  summary: z.string(),
  state: ConfirmationTerminalStateSchema,
  resolvedBy: z.string().optional(),
  resolvedByName: z.string().optional(),
  resolvedAt: z.string(),
});
export type SessionApprovalEventView = z.infer<typeof SessionApprovalEventSchema>;

const SessionCompactionEventSchema = z.object({
  trigger: z.enum(["requested", "automatic"]),
  keptRows: z.number().int().nonnegative(),
});

const SessionGatewayNoticeSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("turn_failure"), runId: z.string() }),
  z.object({ kind: z.literal("schedule_pause"), taskId: z.string() }),
  z.object({
    kind: z.literal("confirmation_unapplied"),
    runId: z.string(),
    confirmationIds: z.array(z.string()),
  }),
]);

/** A single user/assistant turn in a windowed transcript. */
const SessionHistoryEntrySchema = z.object({
  role: z.enum(["user", "assistant"]),
  /**
   * Stable opaque per-entry identifier, unique within the conversation and
   * identical across reads, windows, and gateway restarts. Assistant turns
   * carry the persisted transcript row id; user turns carry the gateway
   * queue-row token their message envelope was stamped with (falling back to
   * the row id when no stamp is present). Attachment annotations join on this
   * value gateway-side. Absent only for entries written before the id stamp
   * existed.
   */
  messageId: z.string().optional(),
  text: z.string(),
  timestamp: z.string(),
  participant: z
    .object({
      platform: z.string(),
      userId: z.string(),
    })
    .optional(),
  /**
   * This entry's content in the order the model produced it: its paragraphs
   * and the positions of the calls between them. A tool block names the call
   * only — `toolEvents` stays the record of what each call did — so ordering
   * and tool state cannot drift apart. Absent on user rows and on entries
   * projected before order was recorded; a reader without it falls back to
   * text-then-tools.
   */
  blocks: z.array(SessionBlockSchema).optional(),
  /**
   * The turn's tool activity in execution order, absent (never an empty
   * array) for entries without tool activity so tolerant readers of the
   * previous shape see byte-identical entries.
   */
  toolEvents: z.array(SessionToolEventSchema).optional(),
  /** Count of events omitted when the turn exceeds the per-entry cap. */
  toolEventsTruncated: z.number().int().positive().optional(),
  /**
   * Decided approvals recorded during this turn, in decision order. Absent
   * (never an empty array) when the turn recorded none, so tolerant readers
   * of the previous shape see byte-identical entries.
   */
  approvalEvents: z.array(SessionApprovalEventSchema).optional(),
  /** Read-side compaction announcement metadata; absent on ordinary messages. */
  compaction: SessionCompactionEventSchema.optional(),
  /** Identifies a Gateway-authored notice rather than model assistant output. */
  gatewayNotice: SessionGatewayNoticeSchema.optional(),
  turnTiming: z.object({ startedAt: z.number(), completedAt: z.number() }).optional(),
  outcome: z.enum(["stopped", "error"]).optional(),
  errorKind: z.enum(["refusal", "timeout", "rate_limit", "context_length", "unknown"]).optional(),
  /**
   * File attachments on this turn (user uploads or assistant outbound files).
   * Absent when no attachments are associated. Contains stable objectKey
   * references suitable for authenticated download.
   */
  attachments: z.array(HubChatAttachmentSchema).optional(),
});
export type SessionHistoryEntryView = z.infer<typeof SessionHistoryEntrySchema>;

// ---------------------------------------------------------------------------
// session.list
// ---------------------------------------------------------------------------

/**
 * `scopeToHubUserId` constrains the returned list to conversations where the
 * named Hub user is a participant. Hub sets this for every caller: specialist
 * configuration authority never grants conversation visibility.
 */
export const SessionListPayloadSchema = z
  .object({
    agentId: AgentIdSchema,
    scopeToHubUserId: z.string().min(1).optional(),
    /** Omitted means `chat`: scheduled runs never arrive unasked. */
    kind: ConversationKindFilterSchema.optional(),
  })
  .strict();
export type SessionListPayload = z.infer<typeof SessionListPayloadSchema>;

export const SessionListDataSchema = z.object({
  conversations: z.array(SessionSummarySchema),
});
export type SessionListData = z.infer<typeof SessionListDataSchema>;

/** Acknowledgement of an archive/unarchive: the conversation's new state. */
export const ConversationArchiveDataSchema = z.object({ archived: z.boolean() });
export type ConversationArchiveData = z.infer<typeof ConversationArchiveDataSchema>;

// ---------------------------------------------------------------------------
// session.history
// ---------------------------------------------------------------------------

export const SessionHistoryPayloadSchema = z
  .object({
    agentId: AgentIdSchema,
    sessionId: z.string().min(1),
    /** Page size of the returned window. */
    limit: z.number().int().positive().max(500).optional(),
    /**
     * Reverse-pagination cursor: a zero-based index into the full transcript.
     * The returned window ends just before this index, so paging older
     * history walks the cursor backwards. Omitted requests the most-recent
     * window.
     */
    before: z.number().int().nonnegative().optional(),
    /**
     * Same participant-scoping rule as `session.list`. When set and the named Hub
     * user is not a participant, the Gateway returns an EMPTY conversation —
     * the identical shape it returns for a session that does not exist — so a
     * non-participant read leaks no existence signal. Hub passes this for
     * Member callers.
     */
    scopeToHubUserId: z.string().min(1).optional(),
  })
  .strict();
export type SessionHistoryPayload = z.infer<typeof SessionHistoryPayloadSchema>;

export const SessionHistoryDataSchema = z.object({
  entries: z.array(SessionHistoryEntrySchema),
  /** True when older entries exist before the returned window. */
  hasMore: z.boolean(),
  /** False when the on-disk JSONL transcript is absent or unreadable. */
  transcriptAvailable: z.boolean(),
});
export type SessionHistoryData = z.infer<typeof SessionHistoryDataSchema>;

// ---------------------------------------------------------------------------
// session.exists
// ---------------------------------------------------------------------------

/**
 * Hub's hub-chat SSE route needs to tell "session row does not exist yet"
 * (fresh UUID — open an empty stream) apart from "session exists but the
 * caller is not a participant"; a scoped `session.history` deliberately cannot,
 * returning the identical empty conversation for both so existence never leaks.
 * This probe answers from session-store metadata alone — deliberately unscoped
 * and transcript-free, so repeated guessed-UUID attempts never cost transcript
 * I/O or ship transcript bytes.
 */
export const SessionExistsPayloadSchema = z
  .object({
    agentId: AgentIdSchema,
    sessionId: z.string().min(1),
    /**
     * Participant scoping for the member-resume path on the hub-chat SSE
     * route. When present, `exists` is true only if the session exists AND
     * the named Hub user is a participant — participation-aware without
     * reading or shipping transcript bytes. The unscoped call keeps its
     * original meaning (pure existence).
     */
    scopeToHubUserId: z.string().min(1).optional(),
  })
  .strict();
export type SessionExistsPayload = z.infer<typeof SessionExistsPayloadSchema>;

export const SessionExistsDataSchema = z.object({
  exists: z.boolean(),
});
export type SessionExistsData = z.infer<typeof SessionExistsDataSchema>;

// ---------------------------------------------------------------------------
// session.live
// ---------------------------------------------------------------------------

/**
 * Mount-time reconcile read for the Hub chat surface: the session's CURRENT
 * turn state, which the persisted transcript cannot carry (it has no
 * "running" concept) and the SSE stream only reveals event-by-event. A page
 * reload is a fresh JS context — without this read a remounted client renders
 * an idle surface while the agent is still working.
 */
export const SessionLivePayloadSchema = z
  .object({
    agentId: AgentIdSchema,
    sessionId: z.string().min(1),
    /**
     * Same participant-scoping rule as `session.history`: when set and the named
     * Hub user is not a participant, the Gateway answers with the idle shape
     * (`turnActive: false`, nothing in flight) — identical to a session that
     * does not exist, so a non-participant read leaks no activity signal.
     */
    scopeToHubUserId: z.string().min(1).optional(),
  })
  .strict();
export type SessionLivePayload = z.infer<typeof SessionLivePayloadSchema>;

/**
 * One in-flight (or just-settled-within-the-turn) tool call from the
 * gateway's per-session tracker — the same redacted/bounded fields the live
 * `tool_status` events carry, so a reconciled row is indistinguishable from
 * one built event-by-event.
 */
const SessionLiveToolCallSchema = z.object({
  toolCallId: z.string(),
  toolName: z.string(),
  paramsSummary: z.string(),
  status: z.enum(["running", "completed", "failed"]),
  argsText: z.string().optional(),
  resultText: z.string().optional(),
});
export type SessionLiveToolCallView = z.infer<typeof SessionLiveToolCallSchema>;

export const SessionLiveDataSchema = z.object({
  /**
   * True while a turn holds the session's dispatcher slot. A turn parked on a
   * confirmation reports FALSE — the slot is released and nothing is
   * executing; the pending confirmation (served alongside by the Hub) is the
   * parked state's visible form.
   */
  turnActive: z.boolean(),
  /**
   * The current turn's tool activity in execution order. Also populated for a
   * turn parked on a confirmation (the tracker retains the parked turn's
   * rows); empty once the turn settles. In-memory state — a gateway restart
   * clears it.
   */
  toolCalls: z.array(SessionLiveToolCallSchema),
});
export type SessionLiveData = z.infer<typeof SessionLiveDataSchema>;
